import React, { useRef, useState, useEffect, useCallback } from 'react';

export default function DrawingCanvas({
  width = 300,
  height = 300,
  guideImage,
  strokeColor = '#000000',
  strokeWidth = 4,
  value,
  onChange,
  disabled = false,
}) {
  const canvasRef = useRef(null);
  const guideCanvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [history, setHistory] = useState([]);
  const [historyIndex, setHistoryIndex] = useState(-1);

  // Initialize canvas with existing value or blank state
  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');

    // Set white background
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, width, height);

    if (value) {
      // Load existing drawing
      const img = new Image();
      img.onload = () => {
        ctx.drawImage(img, 0, 0);
        saveToHistory();
      };
      img.src = value;
    } else {
      saveToHistory();
    }
  }, []);

  // Load guide image if provided
  useEffect(() => {
    if (guideImage && guideCanvasRef.current) {
      const canvas = guideCanvasRef.current;
      const ctx = canvas.getContext('2d');
      const img = new Image();
      img.onload = () => {
        ctx.globalAlpha = 0.3; // Semi-transparent guide
        ctx.drawImage(img, 0, 0, width, height);
      };
      img.src = guideImage;
    }
  }, [guideImage, width, height]);

  const saveToHistory = useCallback(() => {
    const canvas = canvasRef.current;
    const dataUrl = canvas.toDataURL('image/png');

    setHistory((prev) => {
      // Remove any redo states
      const newHistory = prev.slice(0, historyIndex + 1);
      newHistory.push(dataUrl);
      return newHistory;
    });
    setHistoryIndex((prev) => prev + 1);
  }, [historyIndex]);

  const getCoordinates = useCallback((e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;

    if (e.touches && e.touches.length > 0) {
      return {
        x: (e.touches[0].clientX - rect.left) * scaleX,
        y: (e.touches[0].clientY - rect.top) * scaleY,
      };
    }
    return {
      x: (e.clientX - rect.left) * scaleX,
      y: (e.clientY - rect.top) * scaleY,
    };
  }, []);

  const startDrawing = useCallback(
    (e) => {
      if (disabled) return;
      e.preventDefault();

      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');
      const { x, y } = getCoordinates(e);

      ctx.beginPath();
      ctx.moveTo(x, y);
      ctx.strokeStyle = strokeColor;
      ctx.lineWidth = strokeWidth;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';

      setIsDrawing(true);
    },
    [disabled, getCoordinates, strokeColor, strokeWidth]
  );

  const draw = useCallback(
    (e) => {
      if (!isDrawing || disabled) return;
      e.preventDefault();

      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');
      const { x, y } = getCoordinates(e);

      ctx.lineTo(x, y);
      ctx.stroke();
    },
    [isDrawing, disabled, getCoordinates]
  );

  const stopDrawing = useCallback(() => {
    if (!isDrawing) return;

    setIsDrawing(false);
    saveToHistory();

    // Notify parent of change
    const canvas = canvasRef.current;
    const dataUrl = canvas.toDataURL('image/png');
    onChange?.(dataUrl);
  }, [isDrawing, saveToHistory, onChange]);

  const undo = useCallback(() => {
    if (historyIndex <= 0) return;

    const newIndex = historyIndex - 1;
    setHistoryIndex(newIndex);

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const img = new Image();
    img.onload = () => {
      ctx.clearRect(0, 0, width, height);
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, width, height);
      ctx.drawImage(img, 0, 0);
      onChange?.(history[newIndex]);
    };
    img.src = history[newIndex];
  }, [historyIndex, history, width, height, onChange]);

  const redo = useCallback(() => {
    if (historyIndex >= history.length - 1) return;

    const newIndex = historyIndex + 1;
    setHistoryIndex(newIndex);

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const img = new Image();
    img.onload = () => {
      ctx.clearRect(0, 0, width, height);
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, width, height);
      ctx.drawImage(img, 0, 0);
      onChange?.(history[newIndex]);
    };
    img.src = history[newIndex];
  }, [historyIndex, history, width, height, onChange]);

  const clear = useCallback(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, width, height);
    saveToHistory();
    onChange?.(canvas.toDataURL('image/png'));
  }, [width, height, saveToHistory, onChange]);

  return (
    <div className="space-y-3">
      <div className="relative inline-block">
        {/* Guide image layer (if provided) */}
        {guideImage && (
          <canvas
            ref={guideCanvasRef}
            width={width}
            height={height}
            className="absolute top-0 left-0 pointer-events-none"
            style={{ width: '100%', maxWidth: `${width}px`, height: 'auto' }}
          />
        )}

        {/* Drawing canvas */}
        <canvas
          ref={canvasRef}
          width={width}
          height={height}
          className={`border-2 rounded-lg ${
            disabled ? 'border-gray-200 cursor-not-allowed' : 'border-indigo-300 cursor-crosshair'
          }`}
          style={{ width: '100%', maxWidth: `${width}px`, height: 'auto', touchAction: 'none' }}
          onMouseDown={startDrawing}
          onMouseMove={draw}
          onMouseUp={stopDrawing}
          onMouseLeave={stopDrawing}
          onTouchStart={startDrawing}
          onTouchMove={draw}
          onTouchEnd={stopDrawing}
        />
      </div>

      {/* Controls */}
      {!disabled && (
        <div className="flex items-center space-x-2">
          <button
            type="button"
            onClick={undo}
            disabled={historyIndex <= 0}
            className="inline-flex items-center px-3 py-1.5 text-sm font-medium rounded-md border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
            </svg>
            Undo
          </button>

          <button
            type="button"
            onClick={redo}
            disabled={historyIndex >= history.length - 1}
            className="inline-flex items-center px-3 py-1.5 text-sm font-medium rounded-md border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 10h-10a8 8 0 00-8 8v2M21 10l-6 6m6-6l-6-6" />
            </svg>
            Redo
          </button>

          <button
            type="button"
            onClick={clear}
            className="inline-flex items-center px-3 py-1.5 text-sm font-medium rounded-md border border-red-300 bg-white text-red-700 hover:bg-red-50"
          >
            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
            Clear
          </button>
        </div>
      )}
    </div>
  );
}
